import React from 'react';
import ReactDOM from 'react-dom';
import QRCode from 'react-qr-code';
import { Loader2, X, QrCode } from 'lucide-react';
import type { EpayQrInfo } from '../api/billing';

// ePay's qr/generate returns `qrcode` as a raw EMV payload string
// ("000201...KZ.HALYKBANK...") that we must RENDER as a QR ourselves.
// Keep image support in case another environment returns base64 PNG.
function qrImageSrc(raw: string): string | null {
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('iVBOR')) return `data:image/png;base64,${raw}`; // PNG magic in base64
  return null;
}

export default function EpayQrModal({
  qr, polling, onClose, t,
}: {
  qr: EpayQrInfo;
  polling: boolean;
  onClose: () => void;
  t: (k: string) => string;
}) {
  const raw = typeof qr.qrcode === 'string' ? qr.qrcode : '';
  const src = raw ? qrImageSrc(raw) : null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="close">
          <X className="w-5 h-5" />
        </button>
        <h3 className="font-extrabold text-center text-gray-900 mb-1">{t('plans.qrTitle')}</h3>
        <p className="text-sm text-gray-500 text-center mb-5 leading-relaxed">{t('plans.qrSubtitle')}</p>
        <div className="flex items-center justify-center mb-5">
          {src ? (
            <img src={src} alt="Halyk QR" className="w-56 h-56 rounded-2xl border border-gray-100 object-contain" />
          ) : raw ? (
            <div className="w-56 h-56 rounded-2xl border border-gray-100 bg-white p-3 flex items-center justify-center">
              <QRCode value={raw} size={200} aria-label="Halyk QR" />
            </div>
          ) : (
            <div className="w-56 h-56 rounded-2xl border border-gray-100 flex items-center justify-center bg-slate-50">
              <QrCode className="w-16 h-16 text-gray-300" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-center gap-2 text-xs font-bold text-gray-500 mb-4">
          {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="w-2 h-2 rounded-full bg-amber-400" />}
          {t('plans.qrWaiting')}
        </div>
        {(qr.homebankLink || qr.onlinebankLink) && (
          <div className="flex flex-col gap-2">
            {qr.homebankLink && (
              <a href={qr.homebankLink} className="w-full text-center py-2.5 rounded-xl bg-goszakup-50 text-goszakup-700 text-sm font-bold hover:bg-goszakup-100 transition-colors">
                {t('plans.qrOpenHomebank')}
              </a>
            )}
            {qr.onlinebankLink && (
              <a href={qr.onlinebankLink} className="w-full text-center py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50 transition-colors">
                {t('plans.qrOpenBankApp')}
              </a>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
